import { createTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// Vhakacha design system — single source of truth.
//
// Codifies palette (light + dark), typography, spacing, shape, shadows,
// z-index, breakpoints and component defaults so components can stop
// hardcoding values. Brand colours are taken from the app's existing CSS
// custom properties (--orange / --pink in App/styles.css). See UI_AUDIT.md.
// ---------------------------------------------------------------------------

// Brand constants — shared across both colour modes.
// ACCEPTED DESIGN DECISION: white text on the brand orange (#FF7656) is a deliberate
// brand exception that is sub-AA on filled buttons / active filter chips / the dialog
// "Back" button. This is intentional — do NOT "fix" the contrast. (See UI_CHANGELOG.md.)
export const BRAND = {
  orange: '#FF7656',
  orangeLight: '#FF9D86',
  orangeDark: '#E25A3B',
  pink: '#FD4F68',
  pinkLight: '#FF7D90',
  pinkDark: '#D43350',
  // Olive accent used by the Katombora feature banner — a deliberate accent
  // kept outside the coral/pink system.
  olive: '#575E3D',
  // The pink -> orange gradient used for headings / accents.
  gradient: 'linear-gradient(90deg, #FD4F68 0%, #FF7656 100%)',
};

const FONT_FAMILY = [
  'Montserrat',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
].join(',');

const lightPalette = {
  mode: 'light',
  primary: {
    main: BRAND.orange,
    light: BRAND.orangeLight,
    dark: BRAND.orangeDark,
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: BRAND.pink,
    light: BRAND.pinkLight,
    dark: BRAND.pinkDark,
    contrastText: '#FFFFFF',
  },
  error: { main: '#D32F2F' },
  warning: { main: '#ED6C02' },
  info: { main: '#0288D1' },
  success: { main: '#2E7D32' },
  background: {
    default: '#FFFFFF',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#282C34',
    secondary: '#5A6472',
    disabled: 'rgba(40, 44, 52, 0.38)',
  },
  divider: 'rgba(40, 44, 52, 0.12)',
};

const darkPalette = {
  mode: 'dark',
  primary: {
    main: BRAND.orange,
    light: BRAND.orangeLight,
    dark: BRAND.orangeDark,
    contrastText: '#1B1E24',
  },
  secondary: {
    main: BRAND.pink,
    light: BRAND.pinkLight,
    dark: BRAND.pinkDark,
    contrastText: '#1B1E24',
  },
  error: { main: '#F44336' },
  warning: { main: '#FFA726' },
  info: { main: '#29B6F6' },
  success: { main: '#66BB6A' },
  background: {
    default: '#1B1E24',
    paper: '#282C34',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#AEB6C2',
    disabled: 'rgba(255, 255, 255, 0.4)',
  },
  divider: 'rgba(255, 255, 255, 0.12)',
};

const getDesignTokens = (mode) => {
  const palette = mode === 'dark' ? darkPalette : lightPalette;

  return {
    palette: {
      ...palette,
      // Custom brand tokens — read via theme.palette.brand / .brandGradient.
      brand: { orange: BRAND.orange, pink: BRAND.pink },
      brandGradient: BRAND.gradient,
      // Deliberate accent(s) outside the primary/secondary system.
      accent: { olive: BRAND.olive, oliveContrast: '#FFFFFF' },
    },

    shape: {
      borderRadius: 12,
    },

    typography: {
      fontFamily: FONT_FAMILY,
      h1: { fontWeight: 700, fontSize: '3rem', lineHeight: 1.2 },
      h2: { fontWeight: 700, fontSize: '2.25rem', lineHeight: 1.25 },
      h3: { fontWeight: 700, fontSize: '1.875rem', lineHeight: 1.3 },
      h4: { fontWeight: 600, fontSize: '1.5rem', lineHeight: 1.35 },
      h5: { fontWeight: 600, fontSize: '1.25rem', lineHeight: 1.4 },
      h6: { fontWeight: 600, fontSize: '1.125rem', lineHeight: 1.45 },
      subtitle1: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.5 },
      subtitle2: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 },
      body1: { fontWeight: 400, fontSize: '1rem', lineHeight: 1.5 },
      body2: { fontWeight: 400, fontSize: '0.875rem', lineHeight: 1.5 },
      button: { fontWeight: 600, fontSize: '0.875rem', textTransform: 'none' },
      caption: { fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.4 },
      overline: {
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
    },

    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 24, textTransform: 'none' },
        },
      },
      MuiCard: {
        styleOverrides: { root: { borderRadius: 16 } },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 16 } },
      },
      MuiChip: {
        styleOverrides: { root: { fontWeight: 500 } },
      },
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          // Bridge theme tokens to CSS custom properties so the app's
          // hand-written CSS classes (.txt--gray, .txt--sub-heading, ...) and
          // plain inline styles can be colour-mode aware. These re-resolve
          // whenever the theme mode changes, keeping text legible in dark mode.
          ':root': {
            '--vk-text-primary': theme.palette.text.primary,
            '--vk-text-secondary': theme.palette.text.secondary,
            '--vk-divider': theme.palette.divider,
            '--vk-brand': theme.palette.primary.main,
            '--vk-brand-gradient': theme.palette.brandGradient,
            '--vk-accent-olive': theme.palette.accent.olive,
          },
          body: {
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          },
        }),
      },
    },
  };
};

export const themeOption = (mode) => createTheme(getDesignTokens(mode));

// Reusable gradient-text treatment for brand page headings. Paints the brand
// pink->orange gradient *through* the glyphs (background-clip:text), falling back
// to solid brand orange where -webkit-text-fill-color is unsupported.
//
// CONTRAST NOTE: over a white surface the gradient runs ~3.2:1 (pink end) ->
// ~2.6:1 (orange end) — sub-AA even for large text. This is the same accepted
// brand-orange exception documented in UI_CHANGELOG.md (white-on-orange), just
// inverted onto the page background. Over the dark surface it is ~5-6:1 (AA).
// Spread into a Typography `sx` and pair with `component="h1"` for the page title.
export const gradientTextSx = {
  display: 'inline-block',
  color: 'primary.main',
  background: (theme) => theme.palette.brandGradient,
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
};

/**
 * Fix for horizontally scrollable MUI <Tabs>.
 *
 * MUI lays its scroll buttons out inline and keeps the leading one in the flow
 * even while it is disabled, so a row long enough to overflow has its first tab
 * pushed right by the button's width while a row that fits sits flush left —
 * within one page, some filter rows lined up with their heading and others did
 * not. Lifting the buttons out of the flow fixes the alignment, and unlike
 * collapsing the disabled button to zero width it does not make the row jump
 * sideways the moment that button becomes enabled mid-scroll.
 *
 * Horizontal tabs only — a vertical Tabs scrolls with up/down buttons, which
 * these left/right offsets would misplace.
 *
 * Spread into a Tabs `sx`, or into a `styled(Tabs)` argument.
 */
export const scrollableTabsSx = {
  position: 'relative',
  '& .MuiTabs-scrollButtons': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    zIndex: 2,
    width: '2rem',
    '&:first-of-type': { left: 0 },
    '&:last-of-type': { right: 0 },
    // Kept click-through so the tab underneath stays reachable.
    '&.Mui-disabled': {
      opacity: 0,
      pointerEvents: 'none',
    },
  },
};

export default themeOption;
